import { Company } from 'entities/Company/types';
import { ECoUserPermission } from 'permissions';
import { User, UserRoles } from 'entities/User/types';
import { bootstrapImpersonation, getAuthStorage } from 'shared/helpers/impersonation';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// must run before the store is created so persist picks the right storage
bootstrapImpersonation();

type RoleFlags = {
	isAdmin: boolean;
	isUser: boolean;
	isCoUser: boolean;
};

type AuthStore = {
	accessToken: string | null;
	user: User | null;
	company: Company | null;
	permissions: ECoUserPermission[];
	isPasswordExpired: boolean;
} & RoleFlags;

type AuthActions = {
	updateAuthData: (authData: Partial<AuthStore>) => void;
	reset: () => void;
	setPasswordExpired: (expired: boolean) => void;
};

const initialState: AuthStore = {
	accessToken: null,
	user: null,
	company: null,
	permissions: [],
	isPasswordExpired: false,
	isAdmin: false,
	isUser: false,
	isCoUser: false,
};

export const authStore = create<AuthStore & AuthActions>()(
	persist(
		(set) => ({
			...initialState,
			updateAuthData: (authData) =>
				set(() => ({
					...authData,
					isAdmin: authData?.user?.role === UserRoles.admin,
					isUser: authData?.user?.role === UserRoles.user,
					isCoUser: authData?.user?.role === UserRoles.coUser,
				})),

			reset: () => set(() => ({ ...initialState })),
			setPasswordExpired: (expired) => set(() => ({ isPasswordExpired: expired })),
		}),
		{
			name: 'authStore',
			storage: createJSONStorage(getAuthStorage),
		},
	),
);

export const useAuthStore = () => authStore((state) => state);
